import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { motion } from "framer-motion";
import { 
  FileText, TrendingUp, Users, Euro, Calendar, Filter, Download, 
  ArrowUp, ArrowDown, Clock, AlertCircle, ChevronRight, BarChart3, Image
} from "lucide-react";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ComposedChart, Line, ReferenceLine, Legend
} from 'recharts';
import html2canvas from "html2canvas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  if (!value && value !== 0) return "0 €";
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatK = (v) => v >= 1000 ? `${(v/1000).toFixed(0)}k€` : `${v}€`;

// Generate years list
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

// Months list
const months = [
  { value: "01", label: "Janvier" },
  { value: "02", label: "Février" },
  { value: "03", label: "Mars" },
  { value: "04", label: "Avril" },
  { value: "05", label: "Mai" },
  { value: "06", label: "Juin" },
  { value: "07", label: "Juillet" },
  { value: "08", label: "Août" },
  { value: "09", label: "Septembre" },
  { value: "10", label: "Octobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Décembre" },
];

// Blue gradient palette for suppliers
const BLUE_GRADIENT = [
  '#1E40AF', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', 
  '#bfdbfe', '#dbeafe', '#eff6ff', '#f8fafc', '#f1f5f9'
];

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload;
  return (
    <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-xl p-4 text-sm min-w-[200px]">
      <p className="font-semibold text-gray-900 mb-2 pb-2 border-b border-gray-100">{label}</p>
      <div className="space-y-1.5">
        <div className="flex justify-between gap-4">
          <span className="text-gray-500">Montant</span>
          <span className="font-mono font-semibold text-[#1E40AF]">{formatCurrency(data?.total)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-500">Factures</span>
          <span className="font-mono">{data?.count || 0}</span>
        </div>
        {data?.topSupplier && (
          <div className="flex justify-between gap-4 pt-1 border-t border-gray-100 mt-1">
            <span className="text-gray-500">Top fournisseur</span>
            <span className="font-medium text-emerald-600 truncate max-w-[100px]">{data.topSupplier}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendLabel, sparkData, variant = "default", delay = 0 }) => {
  const variants = {
    default: { bg: "from-blue-50 to-indigo-50", border: "border-blue-200", iconBg: "bg-blue-100", iconColor: "text-[#1E40AF]", valueColor: "text-[#1E40AF]" },
    success: { bg: "from-emerald-50 to-green-50", border: "border-emerald-200", iconBg: "bg-emerald-100", iconColor: "text-emerald-600", valueColor: "text-emerald-600" },
    warning: { bg: "from-amber-50 to-orange-50", border: "border-amber-200", iconBg: "bg-amber-100", iconColor: "text-amber-600", valueColor: "text-amber-600" },
    purple: { bg: "from-purple-50 to-violet-50", border: "border-purple-200", iconBg: "bg-purple-100", iconColor: "text-purple-600", valueColor: "text-purple-600" },
    danger: { bg: "from-red-50 to-rose-50", border: "border-red-200", iconBg: "bg-red-100", iconColor: "text-red-500", valueColor: "text-red-500" },
  };
  const v = variants[variant];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
    >
      <Card className={`bg-gradient-to-br ${v.bg} border ${v.border} overflow-hidden h-full`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider font-medium">{title}</p>
              <div className="flex items-baseline gap-2 mt-1">
                <p className={`text-xl sm:text-2xl font-bold font-mono ${v.valueColor}`}>{value}</p>
                {trend !== null && trend !== undefined && !isNaN(trend) && isFinite(trend) && (
                  <span className={`text-xs font-medium flex items-center ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {trend >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                    {Math.abs(trend).toFixed(0)}%
                  </span>
                )}
              </div>
              {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
              {trendLabel && <p className="text-[10px] text-gray-400 mt-0.5">{trendLabel}</p>}
            </div>
            <div className={`p-2 ${v.iconBg} rounded-lg`}>
              <Icon className={`w-5 h-5 ${v.iconColor}`} />
            </div>
          </div>
          {/* Sparkline */}
          {sparkData && sparkData.length > 0 && (
            <div className="mt-3 h-8 flex items-end gap-0.5">
              {sparkData.map((val, i) => {
                const max = Math.max(...sparkData, 1);
                const height = (val / max) * 100;
                return (
                  <div 
                    key={i} 
                    className={`flex-1 rounded-t transition-all ${i === sparkData.length - 1 ? 'bg-[#1E40AF]' : 'bg-gray-300'}`}
                    style={{ height: `${Math.max(height, 5)}%` }}
                  />
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Export function
const exportToPNG = async (elementId, filename) => {
  const element = document.getElementById(elementId);
  if (!element) return;
  const canvas = await html2canvas(element, { backgroundColor: '#ffffff' });
  const link = document.createElement('a');
  link.download = `${filename}.png`;
  link.href = canvas.toDataURL();
  link.click();
};

export default function AchatsOverview() {
  const { token } = useAuth();
  const [allInvoices, setAllInvoices] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [topFournisseurs, setTopFournisseurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllSuppliers, setShowAllSuppliers] = useState(false);
  
  // Filters
  const [yearFilter, setYearFilter] = useState(currentYear.toString());
  const [monthFilter, setMonthFilter] = useState("all");
  const [granularity, setGranularity] = useState("monthly");

  // Fetch all data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const headers = { Authorization: `Bearer ${token}` };
        
        const [invoicesRes, salesRes] = await Promise.all([
          axios.get(`${API}/supplier-invoices?limit=10000`, { headers }),
          axios.get(`${API}/invoices?limit=10000`, { headers })
        ]);
        
        setAllInvoices(invoicesRes.data.invoices || []);
        setSalesData(salesRes.data.invoices || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // Compute previous period data for comparison
  const getPreviousPeriodData = useCallback((invoices, year, month) => {
    let prevYear = year;
    let prevMonth = month;
    
    if (month === "all") {
      prevYear = (parseInt(year) - 1).toString();
    } else {
      const monthNum = parseInt(month);
      if (monthNum === 1) {
        prevMonth = "12";
        prevYear = (parseInt(year) - 1).toString();
      } else {
        prevMonth = String(monthNum - 1).padStart(2, '0');
      }
    }
    
    return invoices.filter(inv => {
      if (!inv.date) return false;
      if (month === "all") {
        return inv.date.startsWith(prevYear);
      } else {
        return inv.date.startsWith(`${prevYear}-${prevMonth}`);
      }
    });
  }, []);

  // Filter and compute stats when filters change
  const { stats, prevStats, monthlyChartData, topSuppliers, achatsVsVentes } = useMemo(() => {
    let invoices = [...allInvoices];
    let sales = [...salesData];
    
    // Apply year filter
    if (yearFilter !== "all") {
      invoices = invoices.filter(inv => inv.date && inv.date.startsWith(yearFilter));
      sales = sales.filter(inv => inv.date && inv.date.startsWith(yearFilter));
    }
    
    // Apply month filter
    if (monthFilter !== "all") {
      if (yearFilter !== "all") {
        invoices = invoices.filter(inv => inv.date && inv.date.startsWith(`${yearFilter}-${monthFilter}`));
        sales = sales.filter(inv => inv.date && inv.date.startsWith(`${yearFilter}-${monthFilter}`));
      } else {
        invoices = invoices.filter(inv => inv.date && inv.date.includes(`-${monthFilter}-`));
        sales = sales.filter(inv => inv.date && inv.date.includes(`-${monthFilter}-`));
      }
    }
    
    setFilteredInvoices(invoices);
    
    // Current period stats
    const totalTTC = invoices.reduce((sum, inv) => sum + (inv.total_ttc || 0), 0);
    const totalCount = invoices.length;
    const uniqueFournisseurs = [...new Set(invoices.map(inv => inv.fournisseur).filter(Boolean))].length;
    const pendingInvoices = invoices.filter(inv => !inv.validated);
    const pendingCount = pendingInvoices.length;
    const pendingAmount = pendingInvoices.reduce((sum, inv) => sum + (inv.total_ttc || 0), 0);
    
    // Calculate average payment delay (mock for now - based on date difference)
    const paidInvoices = invoices.filter(inv => inv.date_paiement && inv.date);
    let avgDelay = 0;
    if (paidInvoices.length > 0) {
      const totalDelay = paidInvoices.reduce((sum, inv) => {
        const invoiceDate = new Date(inv.date);
        const paymentDate = new Date(inv.date_paiement);
        return sum + Math.max(0, (paymentDate - invoiceDate) / (1000 * 60 * 60 * 24));
      }, 0);
      avgDelay = Math.round(totalDelay / paidInvoices.length);
    }
    
    // Previous period for comparison
    const prevInvoices = getPreviousPeriodData(allInvoices, yearFilter, monthFilter);
    const prevTotalTTC = prevInvoices.reduce((sum, inv) => sum + (inv.total_ttc || 0), 0);
    const prevTotalCount = prevInvoices.length;
    
    // Calculate monthly totals with top supplier info
    const monthlyMap = {};
    const fournisseurMap = {};
    
    invoices.forEach(inv => {
      if (inv.date) {
        const date = new Date(inv.date);
        let periodKey, periodLabel;
        
        if (granularity === "quarterly") {
          const quarter = Math.ceil((date.getMonth() + 1) / 3);
          periodKey = `${date.getFullYear()}-Q${quarter}`;
          periodLabel = `T${quarter} ${date.getFullYear().toString().slice(-2)}`;
        } else if (granularity === "yearly") {
          periodKey = date.getFullYear().toString();
          periodLabel = date.getFullYear().toString();
        } else {
          periodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          periodLabel = date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit', timeZone: 'Europe/Paris' });
        }
        
        if (!monthlyMap[periodKey]) {
          monthlyMap[periodKey] = { period: periodLabel, total: 0, count: 0, suppliers: {} };
        }
        monthlyMap[periodKey].total += inv.total_ttc || 0;
        monthlyMap[periodKey].count += 1;
        
        // Track supplier for this period
        const supplier = inv.fournisseur || 'Inconnu';
        monthlyMap[periodKey].suppliers[supplier] = (monthlyMap[periodKey].suppliers[supplier] || 0) + (inv.total_ttc || 0);
      }
      
      // By supplier
      const fournisseur = inv.fournisseur || 'Inconnu';
      if (!fournisseurMap[fournisseur]) {
        fournisseurMap[fournisseur] = { name: fournisseur, total: 0, count: 0 };
      }
      fournisseurMap[fournisseur].total += inv.total_ttc || 0;
      fournisseurMap[fournisseur].count += 1;
    });
    
    // Add top supplier to each period
    Object.values(monthlyMap).forEach(period => {
      const topSupplier = Object.entries(period.suppliers).sort(([,a], [,b]) => b - a)[0];
      period.topSupplier = topSupplier ? topSupplier[0] : null;
      delete period.suppliers;
    });
    
    // Calculate average for reference line
    const monthlyValues = Object.values(monthlyMap).map(d => d.total);
    const avgMonthly = monthlyValues.length > 0 ? monthlyValues.reduce((a, b) => a + b, 0) / monthlyValues.length : 0;
    
    // Sort and prepare monthly data
    const sortedMonthly = Object.entries(monthlyMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12)
      .map(([key, data]) => ({ ...data, key, avg: avgMonthly }));
    
    // Calculate trend line (simple moving average)
    sortedMonthly.forEach((d, i) => {
      if (i >= 2) {
        d.trend = (sortedMonthly[i].total + sortedMonthly[i-1].total + sortedMonthly[i-2].total) / 3;
      }
    });
    
    // Prepare supplier data with previous period comparison
    const prevFournisseurMap = {};
    prevInvoices.forEach(inv => {
      const fournisseur = inv.fournisseur || 'Inconnu';
      prevFournisseurMap[fournisseur] = (prevFournisseurMap[fournisseur] || 0) + (inv.total_ttc || 0);
    });
    
    const sortedFournisseurs = Object.values(fournisseurMap)
      .sort((a, b) => b.total - a.total)
      .map((f, idx) => {
        const prevTotal = prevFournisseurMap[f.name] || 0;
        const evolution = prevTotal > 0 ? ((f.total - prevTotal) / prevTotal) * 100 : null;
        const percentage = totalTTC > 0 ? (f.total / totalTTC) * 100 : 0;
        return { ...f, evolution, percentage, rank: idx + 1 };
      });
    
    // Achats vs Ventes data
    const salesMonthlyMap = {};
    sales.filter(s => s.type === 'Facture').forEach(inv => {
      if (inv.date) {
        const date = new Date(inv.date);
        const periodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const periodLabel = date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit', timeZone: 'Europe/Paris' });
        
        if (!salesMonthlyMap[periodKey]) {
          salesMonthlyMap[periodKey] = { period: periodLabel, ventes: 0 };
        }
        salesMonthlyMap[periodKey].ventes += inv.total_ht || 0;
      }
    });
    
    // Merge achats and ventes
    const achatsVentesData = sortedMonthly.map(d => {
      const salesPeriod = salesMonthlyMap[d.key] || { ventes: 0 };
      const ratio = salesPeriod.ventes > 0 ? (d.total / salesPeriod.ventes) * 100 : 0;
      return {
        period: d.period,
        achats: d.total,
        ventes: salesPeriod.ventes,
        ratio: Math.min(ratio, 100)
      };
    });
    
    return {
      stats: { totalTTC, totalCount, uniqueFournisseurs, pendingCount, pendingAmount, avgDelay, avgBasket: totalCount > 0 ? totalTTC / totalCount : 0 },
      prevStats: { totalTTC: prevTotalTTC, totalCount: prevTotalCount },
      monthlyChartData: sortedMonthly,
      topSuppliers: sortedFournisseurs,
      achatsVsVentes: achatsVentesData
    };
  }, [allInvoices, salesData, yearFilter, monthFilter, granularity, getPreviousPeriodData]);

  // Sparkline data (last 6 months)
  const sparkData = useMemo(() => monthlyChartData.slice(-6).map(d => d.total), [monthlyChartData]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const totalTrend = prevStats.totalTTC > 0 ? ((stats.totalTTC - prevStats.totalTTC) / prevStats.totalTTC) * 100 : null;
  const countTrend = prevStats.totalCount > 0 ? ((stats.totalCount - prevStats.totalCount) / prevStats.totalCount) * 100 : null;
  
  // Top 6 concentration
  const top6Total = topSuppliers.slice(0, 6).reduce((sum, s) => sum + s.total, 0);
  const top6Percentage = stats.totalTTC > 0 ? (top6Total / stats.totalTTC) * 100 : 0;

  return (
    <div className="p-4 md:p-6 space-y-8" data-testid="achats-overview">
      {/* Header with filters */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">
            Vue d'ensemble Achats
          </h1>
          <p className="text-muted-foreground mt-1">
            Synthèse de vos dépenses fournisseurs
          </p>
        </div>
        
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-1 border">
            <span className="text-xs text-gray-500 pl-2">Année</span>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-24 border-0 bg-transparent h-8" data-testid="year-filter">
                <SelectValue placeholder="Année" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {years.map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-1 border">
            <span className="text-xs text-gray-500 pl-2">Mois</span>
            <Select value={monthFilter} onValueChange={setMonthFilter}>
              <SelectTrigger className="w-28 border-0 bg-transparent h-8" data-testid="month-filter">
                <SelectValue placeholder="Mois" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {months.map(month => (
                  <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button variant="outline" size="sm" className="h-10 gap-2" onClick={() => exportToPNG('achats-dashboard', 'Achats_Overview')}>
            <Download className="w-4 h-4" />
            Exporter PDF
          </Button>
        </div>
      </div>

      <div id="achats-dashboard" className="space-y-8">
        {/* KPI Cards - 6 cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <KPICard
            title="Total Achats"
            value={formatCurrency(stats.totalTTC)}
            trend={totalTrend}
            trendLabel="vs période précédente"
            icon={Euro}
            variant="default"
            sparkData={sparkData}
            delay={0}
          />
          <KPICard
            title="Factures"
            value={stats.totalCount}
            trend={countTrend}
            icon={FileText}
            variant="success"
            sparkData={sparkData}
            delay={0.05}
          />
          <KPICard
            title="Fournisseurs"
            value={stats.uniqueFournisseurs}
            subtitle="actifs sur la période"
            icon={Users}
            variant="purple"
            delay={0.1}
          />
          <KPICard
            title="Moyenne/Facture"
            value={formatCurrency(stats.avgBasket)}
            icon={TrendingUp}
            variant="warning"
            delay={0.15}
          />
          <KPICard
            title="En attente"
            value={stats.pendingCount}
            subtitle={formatCurrency(stats.pendingAmount)}
            icon={AlertCircle}
            variant={stats.pendingCount > 5 ? "danger" : "default"}
            delay={0.2}
          />
          <KPICard
            title="Délai paiement"
            value={`${stats.avgDelay} j`}
            subtitle="délai moyen"
            icon={Clock}
            variant={stats.avgDelay > 30 ? "danger" : "success"}
            delay={0.25}
          />
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Evolution Chart */}
          <Card className="border shadow-sm overflow-hidden">
            <CardHeader className="pb-2 bg-gray-50/50 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <div className="p-1.5 bg-blue-100 rounded-lg">
                    <Calendar className="w-4 h-4 text-[#1E40AF]" />
                  </div>
                  Évolution des achats
                </CardTitle>
                <div className="flex items-center gap-2">
                  {/* Granularity Toggle */}
                  <div className="flex rounded-full border border-gray-200 overflow-hidden bg-gray-50 p-0.5">
                    {[{v: 'monthly', l: 'Mois'}, {v: 'quarterly', l: 'Trim.'}, {v: 'yearly', l: 'Année'}].map(({v, l}) => (
                      <button
                        key={v}
                        onClick={() => setGranularity(v)}
                        className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                          granularity === v 
                            ? 'bg-[#1E40AF] text-white shadow-sm' 
                            : 'text-gray-600 hover:text-[#1E40AF]'
                        }`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-gray-400 hover:text-[#1E40AF]" onClick={() => exportToPNG('chart-evolution-achats', 'Evolution_Achats')}>
                    <Image className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4" id="chart-evolution-achats">
              {monthlyChartData.length > 0 ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={monthlyChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="barGradientAchats" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#1E40AF" stopOpacity={1}/>
                          <stop offset="100%" stopColor="#10b981" stopOpacity={0.8}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis dataKey="period" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
                      <YAxis stroke="#9ca3af" fontSize={11} tickFormatter={formatK} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} width={50} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend iconType="circle" wrapperStyle={{ paddingTop: '10px' }} />
                      {/* Average Line */}
                      {monthlyChartData.length > 0 && (
                        <ReferenceLine 
                          y={monthlyChartData[0]?.avg} 
                          stroke="#9ca3af" 
                          strokeDasharray="5 5" 
                          strokeWidth={1.5}
                          label={{ value: 'Moyenne', position: 'right', fontSize: 10, fill: '#9ca3af' }}
                        />
                      )}
                      <Bar dataKey="total" name="Achats" fill="url(#barGradientAchats)" radius={[6, 6, 0, 0]} maxBarSize={45} />
                      <Line type="monotone" dataKey="trend" name="Tendance" stroke="#10b981" strokeWidth={2.5} dot={false} strokeDasharray="0" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[288px] text-muted-foreground">
                  Aucune donnée disponible
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Fournisseurs - Horizontal Bars */}
          <Card className="border shadow-sm overflow-hidden">
            <CardHeader className="pb-2 bg-gray-50/50 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <div className="p-1.5 bg-purple-100 rounded-lg">
                    <Users className="w-4 h-4 text-purple-600" />
                  </div>
                  Top Fournisseurs
                </CardTitle>
                <Badge variant="secondary" className="text-xs">
                  Top {showAllSuppliers ? 10 : 6} = {top6Percentage.toFixed(0)}% du total
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {topSuppliers.length > 0 ? (
                <div className="space-y-3">
                  {topSuppliers.slice(0, showAllSuppliers ? 10 : 6).map((supplier, idx) => (
                    <div key={supplier.name} className="group">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="text-xs font-bold text-gray-400 w-5">#{idx + 1}</span>
                          <span className="text-sm font-medium truncate">{supplier.name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          {supplier.evolution !== null && (
                            <span className={`text-xs font-medium flex items-center ${supplier.evolution >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                              {supplier.evolution >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                              {Math.abs(supplier.evolution).toFixed(0)}%
                            </span>
                          )}
                          <span className="text-xs text-gray-500">{supplier.percentage.toFixed(1)}%</span>
                          <span className="text-sm font-bold font-mono text-gray-700 w-20 text-right">{formatCurrency(supplier.total)}</span>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${supplier.percentage}%`,
                            backgroundColor: BLUE_GRADIENT[Math.min(idx, BLUE_GRADIENT.length - 1)]
                          }}
                        />
                      </div>
                    </div>
                  ))}
                  
                  {topSuppliers.length > 6 && (
                    <button 
                      onClick={() => setShowAllSuppliers(!showAllSuppliers)}
                      className="w-full py-2 text-sm text-[#1E40AF] hover:text-[#1E40AF]/80 font-medium flex items-center justify-center gap-1 mt-2"
                    >
                      {showAllSuppliers ? 'Voir moins' : 'Voir tous les fournisseurs'}
                      <ChevronRight className={`w-4 h-4 transition-transform ${showAllSuppliers ? 'rotate-90' : ''}`} />
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                  Aucune donnée disponible
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Achats vs Ventes Section */}
        <Card className="border shadow-sm overflow-hidden">
          <CardHeader className="pb-2 bg-gray-50/50 border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <div className="p-1.5 bg-emerald-100 rounded-lg">
                  <BarChart3 className="w-4 h-4 text-emerald-600" />
                </div>
                Achats vs Ventes
              </CardTitle>
              <Button variant="ghost" size="sm" className="h-8 px-2 text-gray-400 hover:text-[#1E40AF]" onClick={() => exportToPNG('chart-achats-ventes', 'Achats_vs_Ventes')}>
                <Image className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4" id="chart-achats-ventes">
            {achatsVsVentes.length > 0 ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={achatsVsVentes} margin={{ top: 10, right: 50, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="period" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
                    <YAxis yAxisId="left" stroke="#9ca3af" fontSize={11} tickFormatter={formatK} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} width={50} />
                    <YAxis yAxisId="right" orientation="right" stroke="#f59e0b" fontSize={11} tickFormatter={v => `${v}%`} domain={[0, 100]} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} width={40} />
                    <Tooltip 
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                      formatter={(value, name) => [name === 'Ratio' ? `${value.toFixed(1)}%` : formatCurrency(value), name]}
                    />
                    <Legend iconType="circle" wrapperStyle={{ paddingTop: '10px' }} />
                    <Bar yAxisId="left" dataKey="achats" name="Achats" fill="#1E40AF" radius={[4, 4, 0, 0]} maxBarSize={30} />
                    <Bar yAxisId="left" dataKey="ventes" name="Ventes" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={30} />
                    <Line yAxisId="right" type="monotone" dataKey="ratio" name="Ratio Achats/CA" stroke="#f59e0b" strokeWidth={2.5} dot={{ fill: '#f59e0b', r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[288px] text-muted-foreground">
                Aucune donnée disponible
              </div>
            )}
            <p className="text-xs text-center text-gray-400 mt-3">
              Comparaison mensuelle entre les achats fournisseurs et le CA réalisé. La courbe orange indique le ratio Achats/CA.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
